import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
} from 'react-native';

import { CityDistrictSelect } from '../../components/CityDistrictSelect';
import { MonthYearSelect } from '../../components/MonthYearSelect';
import { useAuth } from '../../contexts/AuthContext';
import { saveUserProfile } from '../../services/userService';
import type { BabyGender, UserRole } from '../../types/models';
import { birthYearOptions } from '../../utils/birthPickerData';

const ROLES: { key: UserRole; label: string }[] = [
  { key: 'mother', label: 'Anne' },
  { key: 'father', label: 'Baba' },
  { key: 'caregiver', label: 'Bakıcı' },
];

export function ProfileSetupScreen() {
  const { user, refreshProfile } = useAuth();
  const [role, setRole] = useState<UserRole>('mother');
  const [displayName, setDisplayName] = useState('');
  const [parentAge, setParentAge] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [babyGender, setBabyGender] = useState<BabyGender>('girl');
  const [city, setCity] = useState('');
  const [district, setDistrict] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const allowedYears = useMemo(() => new Set(birthYearOptions()), []);

  const canSubmit = useMemo(() => {
    const dn = displayName.trim();
    const c = city.trim();
    const d = district.trim();
    const m = parseInt(birthMonth, 10);
    const y = birthYear.trim();
    const pa = parseInt(parentAge, 10);
    if (!dn || !c || !d || !birthMonth || !y) {
      return false;
    }
    if (Number.isNaN(pa) || pa < 16 || pa > 80) {
      return false;
    }
    if (Number.isNaN(m) || m < 1 || m > 12) {
      return false;
    }
    if (!allowedYears.has(y)) {
      return false;
    }
    return true;
  }, [displayName, city, district, birthMonth, birthYear, parentAge, allowedYears]);

  async function onSubmit() {
    if (!user?.uid || !canSubmit) {
      return;
    }
    setError(null);
    const y = parseInt(birthYear, 10);
    const m = parseInt(birthMonth, 10);
    const pa = parseInt(parentAge, 10);
    const babyBirthDate = new Date(y, m - 1, 1);
    setBusy(true);
    try {
      await saveUserProfile(user.uid, {
        role,
        displayName: displayName.trim(),
        parentAge: pa,
        babyBirthDate,
        babyGender,
        city: city.trim(),
        district: district.trim(),
      });
      await refreshProfile();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Kayıt başarısız';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
 className="flex-1 bg-white dark:bg-black">
      <ScrollView
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{
          flexGrow: 1,
          paddingHorizontal: 24,
          paddingTop: 24,
          paddingBottom: 48,
        }}>
 <Text className="text-2xl font-bold text-gray-800 mb-1">Profilini oluştur</Text>
 <Text className="text-gray-500 text-base mb-6">
          Bu bilgiler toplulukta eşleşme için kullanılır; birkaç dakikanı alır.
        </Text>

        {error ? (
 <View className="mb-4 rounded-2xl border border-zinc-300 bg-zinc-100 dark:bg-zinc-900 px-4 py-3">
 <Text className="text-center text-sm text-zinc-800">{error}</Text>
          </View>
        ) : null}

 <Text className="text-gray-800 font-medium mb-2">Rolün</Text>
 <View className="mb-5 flex-row flex-wrap gap-2">
          {ROLES.map(r => (
            <TouchableOpacity
              key={r.key}
              activeOpacity={0.85}
              onPress={() => setRole(r.key)}
              className={`min-w-[28%] flex-1 rounded-2xl border px-4 py-4 ${
                role === r.key
                  ? 'border-zinc-900 bg-zinc-900 dark:border-zinc-50 dark:bg-zinc-50'
                  : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900'
              }`}>
              <Text
                className={`text-center text-sm font-semibold ${
                  role === r.key ? 'text-white dark:text-zinc-950' : 'text-zinc-800 dark:text-zinc-200'
                }`}>
                {r.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

 <Text className="text-gray-800 font-medium mb-2">Gösterilecek isim (rumuz veya ad)</Text>
        <TextInput
 className="mb-4 rounded-2xl border border-zinc-200 bg-white px-4 py-3.5 text-base text-gray-800"
          placeholder="Örn. Can'ın annesi"
          placeholderTextColor="#9ca3af"
          value={displayName}
          onChangeText={t => {
            setError(null);
            setDisplayName(t);
          }}
        />

 <Text className="text-gray-800 font-medium mb-2">Ebeveyn yaşı</Text>
        <TextInput
 className="mb-4 rounded-2xl border border-zinc-200 bg-white px-4 py-3.5 text-base text-gray-800"
          placeholder="Örn. 32"
          placeholderTextColor="#9ca3af"
          keyboardType="number-pad"
          value={parentAge}
          onChangeText={t => {
            setError(null);
            setParentAge(t);
          }}
        />

        <MonthYearSelect
          month={birthMonth}
          year={birthYear}
          onMonthChange={m => {
            setError(null);
            setBirthMonth(m);
          }}
          onYearChange={y => {
            setError(null);
            setBirthYear(y);
          }}
        />

 <Text className="text-gray-800 font-medium mb-2">Bebek cinsiyeti</Text>
 <View className="mb-5 flex-row gap-3">
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => setBabyGender('girl')}
            className={`flex-1 rounded-2xl border py-3.5 ${
              babyGender === 'girl'
                ? 'border-zinc-900 bg-zinc-900 dark:border-zinc-50 dark:bg-zinc-50'
                : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900'
            }`}>
            <Text
              className={`text-center text-base font-semibold ${
                babyGender === 'girl' ? 'text-white dark:text-zinc-950' : 'text-zinc-800 dark:text-zinc-200'
              }`}>
              Kız
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            activeOpacity={0.85}
            onPress={() => setBabyGender('boy')}
            className={`flex-1 rounded-2xl border py-3.5 ${
              babyGender === 'boy'
                ? 'border-zinc-900 bg-zinc-900 dark:border-zinc-50 dark:bg-zinc-50'
                : 'border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900'
            }`}>
            <Text
              className={`text-center text-base font-semibold ${
                babyGender === 'boy' ? 'text-white dark:text-zinc-950' : 'text-zinc-800 dark:text-zinc-200'
              }`}>
              Erkek
            </Text>
          </TouchableOpacity>
        </View>

        <CityDistrictSelect
          city={city}
          district={district}
          onCityChange={c => {
            setError(null);
            setCity(c);
          }}
          onDistrictChange={d => {
            setError(null);
            setDistrict(d);
          }}
          theme="rose"
        />

        <TouchableOpacity
          activeOpacity={0.85}
          onPress={onSubmit}
          disabled={!canSubmit || busy}
          className={`rounded-2xl border border-zinc-900 py-4 dark:border-zinc-50 ${
            !canSubmit || busy ? 'bg-zinc-300 dark:bg-zinc-700' : 'bg-zinc-900 dark:bg-zinc-50'
          }`}>
          <Text className="text-center text-base font-semibold text-white dark:text-zinc-950">
            {busy ? 'Kaydediliyor…' : 'Kaydet ve devam et'}
          </Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );
}
